import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import {
  TMarketIdentifiers,
  TSessionData,
} from '@/core/constants/event_types/base_mable_event';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { UniqueIdUtils } from '@/core/event_logic/utils/unique_id_utils';

import {
  CustomerDataFields,
  ICustomerDataFields,
} from '../customer_data_new/types';
import { IpDataHandler } from '../ip_data';
import { IpAddressData } from '../ip_data/types';
import { MarketDataHandler } from '../market';

import { TPlatformIdentifiers } from '../../constants/event_types/base_mable_event';
import { DictUtils } from '../../event_logic/utils/dict_utils';

export class SessionDataHandler {
  private static _storageHandler: StorageHandler<TSessionData>;

  private static async initStorageHandler(): Promise<void> {
    if (!this._storageHandler) {
      const initialTime = await TimeUtils.getCurrentEpochInSecondsSynced();
      this._storageHandler = new StorageHandler<TSessionData>(
        'sessionData',
        {
          cdfs: [],
          ip4: '',
          ip6: '',
          iqpar: {},
          is_backend: false,
          mid: {
            do: '',
          },
          pids: {
            epik: [],
            fbclid: [],
            gclid: [],
            ttclid: [],
          },
          rl: '',
          sct: 0,
          sid: '',
          ssts: initialTime,
        },
        EBrowserStorageType.LOCALSTORAGE,
      );
    }
  }

  static async getStorageHandler(): Promise<StorageHandler<TSessionData>> {
    await this.initStorageHandler();
    return this._storageHandler;
  }

  private static validateAndFixIqpar(sessionData: TSessionData): TSessionData {
    return DictUtils.validateAndFixStringArrayDict(sessionData, 'iqpar');
  }

  static async updateIdentityData(
    cdfs: ICustomerDataFields[],
    pids: TPlatformIdentifiers,
  ): Promise<TSessionData> {
    const handler = await this.getStorageHandler();
    const sessionData = this.validateAndFixIqpar(handler.get());
    sessionData.cdfs = cdfs as CustomerDataFields[];
    sessionData.pids = pids;
    handler.set(sessionData);
    return sessionData;
  }

  /**
   * Initialize or load and maybe update the session data
   *
   * @param browserApiData - Browser API Data
   * @param ipData - IP Address Data
   * @param resetSession - Reset Session
   * @returns Session Data and Session Data Changed
   * @dependencies
   * - MarketDataHandler.getMarketData() - Used when resetting session to get fresh market data
   * - MarketDataHandler.updateProvidedMarketData() - Used to update existing market data
   */
  static async initOrLoadAndMaybeUpdate(
    browserApiData: TBrowserApiData,
    ipData?: IpAddressData,
    resetSession?: boolean,
    mktData?: TMarketIdentifiers,
  ): Promise<{ sessionData: TSessionData; sessionDataChanged: boolean }> {
    const handler = await this.getStorageHandler();
    let sessionData = this.validateAndFixIqpar(handler.get());
    const currentEpoch = await TimeUtils.getCurrentEpochInSecondsSynced();

    if (resetSession && sessionData?.sct) {
      const sid = UniqueIdUtils.getUuidV7();
      sessionData = {
        cdfs: [],
        ip4: ipData?.v4 ?? '',
        ip6: ipData?.v6 ?? '',
        iqpar: browserApiData.qpar,
        is_backend: false,
        mid: mktData ?? undefined,
        pids: {
          epik: [],
          fbclid: [],
          gclid: [],
          ttclid: [],
        },
        rl: '',
        sct: sessionData.sct ? sessionData.sct + 1 : 1,
        sid: sid,
        ssts: currentEpoch,
      };
    } else if (sessionData.sct) {
      let sessionDataChanged = false;
      const {
        ipv4,
        ipv6,
        isUpdated: isUpdatedIP,
      } = IpDataHandler.updateIPData(ipData, sessionData.ip4, sessionData.ip6);
      if (isUpdatedIP) {
        sessionData.ip4 = ipv4;
        sessionData.ip6 = ipv6;
        sessionDataChanged = true;
      }

      const { isUpdated: isUpdatedMkt, mktData: updatedMktData } =
        MarketDataHandler.updateProvidedMarketData(mktData, sessionData.mid);
      if (isUpdatedMkt) {
        sessionData.mid = updatedMktData;
        sessionDataChanged = true;
      }

      handler.set(sessionData);
      if (!sessionDataChanged) {
        return { sessionData, sessionDataChanged: false };
      }
    } else {
      const sid = UniqueIdUtils.getUuidV7();
      sessionData = {
        cdfs: [],
        ip4: ipData ? ipData.v4 : '',
        ip6: ipData ? ipData.v6 : '',
        iqpar: browserApiData.qpar,
        is_backend: false,
        mid: mktData ?? undefined,
        pids: {
          epik: [],
          fbclid: [],
          gclid: [],
          ttclid: [],
        },
        rl: browserApiData.rl,
        sct: 1,
        sid: sid,
        ssts: currentEpoch,
      };
    }
    handler.set(sessionData);
    return { sessionData, sessionDataChanged: true };
  }
}
